import { useCallback, useEffect, useState } from "react";
import type { AccessRoleView, Branch } from "@dentacore/shared-types";

import { listRoles } from "../api/access";
import { listBranches } from "../api/branches";
import { useAuth } from "../auth/auth-context";
import { formatMoney, minorUnitsPerUnit } from "../patients/money";

/**
 * What a workflow's settings name by id — the clinic's roles and locations —
 * read once for the page and the editor, plus money in the clinic's currency.
 *
 * Roles are read with `people.view` (the access controller's rule); someone
 * without it still sees the page, with roles counted rather than named.
 */
export interface WorkflowRefs {
  roles: AccessRoleView[] | null;
  branches: Branch[] | null;
  roleName: (id: string | null | undefined) => string;
  branchName: (id: string) => string;
  /** Minor units in the clinic's home currency, as words. */
  money: (minor: number) => string;
  /** What was typed in a money box (whole units), as minor units. */
  toMinor: (typed: number) => number;
  /** Minor units, as the number a money box shows. */
  fromMinor: (minor: number) => number;
  currency: string;
}

export function useWorkflowRefs(): WorkflowRefs {
  const { user } = useAuth();
  // Always set inside the shell; empty only before sign-in, when nothing here draws.
  const currency = user?.currency ?? "";
  const [roles, setRoles] = useState<AccessRoleView[] | null>(null);
  const [branches, setBranches] = useState<Branch[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    listRoles(controller.signal)
      .then(setRoles)
      .catch(() => {
        if (!controller.signal.aborted) setRoles([]);
      });
    listBranches(controller.signal)
      .then(setBranches)
      .catch(() => {
        if (!controller.signal.aborted) setBranches([]);
      });
    return () => controller.abort();
  }, []);

  const roleName = useCallback(
    (id: string | null | undefined) =>
      id === null || id === undefined
        ? "nobody yet"
        : (roles?.find((r) => r.id === id)?.name ?? "a role"),
    [roles],
  );
  const branchName = useCallback(
    (id: string) => branches?.find((b) => b.id === id)?.name ?? "a location",
    [branches],
  );
  const per = minorUnitsPerUnit(currency);
  const money = useCallback((minor: number) => formatMoney(minor, currency), [currency]);
  const toMinor = useCallback((typed: number) => Math.round(typed * per), [per]);
  const fromMinor = useCallback((minor: number) => minor / per, [per]);

  return { roles, branches, roleName, branchName, money, toMinor, fromMinor, currency };
}
